import { createApi } from '@reduxjs/toolkit/query/react'
import config from '@/config'
import { axiosBaseQuery } from '../helpers'

type DefaultsResponse = Record<
  string,
  {
    title: string
    preferences: Record<
      string,
      {
        label: string
        description: string
      }
    >
  }
>

type UserPreferencesResponse = Record<
  string,
  {
    email: boolean
    push: boolean
  }
>

interface PreferencesWrapper {
  preferences: UserPreferencesResponse
}

const service = 'notifications'

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: config.url.notifications,
    service,
  }),
  tagTypes: ['Preferences', 'Defaults'],
  endpoints: (build) => ({
    getDefaults: build.query<DefaultsResponse, void>({
      query: () => ({
        url: '/defaults',
        method: 'GET',
      }),
      providesTags: ['Defaults'],
    }),
    getPreferences: build.query<UserPreferencesResponse, void>({
      query: () => ({
        url: '/preferences',
        method: 'GET',
      }),
      transformResponse: (response: PreferencesWrapper) => response.preferences,
      providesTags: ['Preferences'],
    }),
    updatePreferences: build.mutation<
      UserPreferencesResponse,
      UserPreferencesResponse
    >({
      query: (preferences: any) => ({
        url: '/preferences',
        method: 'PUT',
        data: { preferences },
      }),
      transformResponse: (response: PreferencesWrapper) => response.preferences,
      async onQueryStarted(preferences, { dispatch, queryFulfilled }) {
        // Optimistic update
        const patchResult = dispatch(
          notificationsApi.util.updateQueryData(
            'getPreferences',
            undefined,
            (draft: any) => {
              Object.assign(draft, preferences)
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          // Revert on error
          patchResult.undo()
        }
      },
      invalidatesTags: ['Preferences'],
    }),
    sendTestNotification: build.mutation<
      void,
      { organization_id: string; preference_type: string }
    >({
      query: (data: any) => ({
        url: '/send-test-notification',
        method: 'POST',
        data,
      }),
    }),
  }),
})

export const {
  useGetDefaultsQuery,
  useGetPreferencesQuery,
  useUpdatePreferencesMutation,
  useSendTestNotificationMutation,
} = notificationsApi
